import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const MayorDetallado = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [mayorFechaDesde, setMayorFechaDesde] = useState("");
  const [mayorFechaHasta, setMayorFechaHasta] = useState("");
  const [mayorEstatusPago, setMayorEstatusPago] = useState("todos");
  const [mayorOficina, setMayorOficina] = useState("todas");
  const [mayorTipoFecha, setMayorTipoFecha] = useState("planilla");
  const [mayorFilters, setMayorFilters] = useState({ planilla: "", rif: "", razon: "", fecha: "", tipo: "", base: "", importe: "", multas: "", descuento: "", monto: "" });

  useEffect(() => { fetchMayorImpuestoData(); }, []);

  const fetchMayorImpuestoData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      let url = `${API}/reportes/mayor-impuesto-detallado`;
      const params = new URLSearchParams();
      if (aplicarFiltros || mayorFechaDesde || mayorFechaHasta || mayorEstatusPago !== "todos" || mayorOficina !== "todas") {
        if (mayorFechaDesde) params.append("fecha_desde", mayorFechaDesde);
        if (mayorFechaHasta) params.append("fecha_hasta", mayorFechaHasta);
        if (mayorEstatusPago !== "todos") params.append("estatus_pago", mayorEstatusPago);
        if (mayorOficina !== "todas") params.append("oficina", mayorOficina);
        if (mayorTipoFecha !== "planilla") params.append("tipo_fecha", mayorTipoFecha);
      }
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados");
    } catch {
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosMayor = () => {
    setMayorFechaDesde("");
    setMayorFechaHasta("");
    setMayorEstatusPago("todos");
    setMayorOficina("todas");
    setMayorTipoFecha("planilla");
    setMayorFilters({ planilla: "", rif: "", razon: "", fecha: "", tipo: "", base: "", importe: "", multas: "", descuento: "", monto: "" });
    fetchMayorImpuestoData(false);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosMayorActivos = mayorFechaDesde || mayorFechaHasta || mayorEstatusPago !== "todos" || mayorOficina !== "todas" || mayorTipoFecha !== "planilla";

  const updateMayorFilter = (key, value) => setMayorFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosColumnaActivos = Object.values(mayorFilters).some(v => v !== "");

  const filteredMayorFilas = reporteData?.filas?.filter(f => {
    const mf = mayorFilters;
    if (mf.planilla && !(f.nro_planilla || "").toLowerCase().includes(mf.planilla.toLowerCase())) return false;
    if (mf.rif && !(f.rif || "").toLowerCase().includes(mf.rif.toLowerCase())) return false;
    if (mf.razon && !(f.nombre_razon_social || "").toLowerCase().includes(mf.razon.toLowerCase())) return false;
    if (mf.fecha && !(f.fecha || "").includes(mf.fecha)) return false;
    if (mf.tipo && !(f.tipo_impuesto || "").toLowerCase().includes(mf.tipo.toLowerCase())) return false;
    if (mf.base && !String(f.base_imponible || 0).includes(mf.base)) return false;
    if (mf.importe && !String(f.importe || 0).includes(mf.importe)) return false;
    if (mf.multas && !String(f.multas || 0).includes(mf.multas)) return false;
    if (mf.descuento && !String(f.descuento || 0).includes(mf.descuento)) return false;
    if (mf.monto && !String(f.monto_declarado || 0).includes(mf.monto)) return false;
    return true;
  }) || [];

  const mayorTotalesFiltrados = (() => {
    const base = { base_imponible: 0, importe: 0, multas: 0, descuento: 0, monto_declarado: 0 };
    const planillasContadas = new Set();
    filteredMayorFilas.forEach(f => {
      base.base_imponible += (f.base_imponible || 0);
      base.importe += (f.importe || 0);
      // Multas, descuento y monto declarado se cuentan UNA vez por planilla
      if (!planillasContadas.has(f.nro_planilla)) {
        base.multas += (f.multas || 0);
        base.descuento += (f.descuento || 0);
        base.monto_declarado += (f.total_planilla || 0);
        planillasContadas.add(f.nro_planilla);
      }
    });
    return base;
  })();


  const exportMayorToExcel = () => {
    if (!reporteData) return;
    const rows = filteredMayorFilas;
    const wsData = [
      ["MAYOR DE IMPUESTO DETALLADO"],
      [`Generado: ${new Date(reporteData.fecha_generacion).toLocaleString("es-VE")}`],
      [],
      ["NRO PLANILLA", "RIF", "RAZON SOCIAL", "FECHA LIQUIDACION", "TIPO IMPUESTO/TASA", "BASE IMPONIBLE", "IMPORTE", "MULTAS", "DESCUENTO", "MONTO DECLARADO", "OFICINA", "ESTATUS PAGO"]
    ];
    rows.forEach(f => wsData.push([f.nro_planilla, f.rif, f.nombre_razon_social, f.fecha, f.tipo_impuesto, f.base_imponible, f.importe, f.multas, f.descuento, f.monto_declarado, f.oficina, f.estatus_pago]));
    wsData.push([]);
    wsData.push(["", "", "", "", "TOTALES", mayorTotalesFiltrados.base_imponible, mayorTotalesFiltrados.importe, mayorTotalesFiltrados.multas, mayorTotalesFiltrados.descuento, mayorTotalesFiltrados.monto_declarado]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 16 }, { wch: 14 }, { wch: 30 }, { wch: 14 }, { wch: 28 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, "MayorImpuesto");
    XLSX.writeFile(wb, `Mayor_Impuesto_Detallado_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportMayorToPDF = () => {
    if (!reporteData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text("MAYOR DE IMPUESTO DETALLADO", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generado: ${new Date(reporteData.fecha_generacion).toLocaleString("es-VE")}`, 14, 25);
    const body = filteredMayorFilas.map(f => [f.nro_planilla, f.rif, (f.nombre_razon_social || "").substring(0, 22), f.fecha, (f.tipo_impuesto || "").substring(0, 22), fmtBs(f.base_imponible), (f.importe || 0).toFixed(2), fmtBs(f.multas), fmtBs(f.descuento), fmtBs(f.monto_declarado)]);
    body.push(["", "", "", "", "TOTALES", fmtBs(mayorTotalesFiltrados.base_imponible), mayorTotalesFiltrados.importe.toFixed(2), fmtBs(mayorTotalesFiltrados.multas), fmtBs(mayorTotalesFiltrados.descuento), fmtBs(mayorTotalesFiltrados.monto_declarado)]);
    doc.autoTable({
      startY: 32,
      head: [["Planilla", "RIF", "Razon Social", "Fecha", "Tipo Imp/Tasa", "Base Imp.", "Importe", "Multas", "Descuento", "Monto Decl."]],
      body,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      alternateRowStyles: { fillColor: [238, 242, 255] },
      didParseCell: (data) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [224, 231, 255];
        }
      }
    });
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(`SIRET - Mayor de Impuesto Detallado | Pagina ${i} de ${pages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }
    doc.save(`Mayor_Impuesto_Detallado_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportMayorToCSV = () => {
    if (!reporteData) return;
    const header = "NRO PLANILLA,RIF,RAZON SOCIAL,FECHA,TIPO IMPUESTO,BASE IMPONIBLE,IMPORTE,MULTAS,DESCUENTO,MONTO DECLARADO,OFICINA,ESTATUS PAGO";
    const rows = filteredMayorFilas.map(f => `"${f.nro_planilla}","${f.rif}","${f.nombre_razon_social}","${f.fecha}","${f.tipo_impuesto}",${f.base_imponible},${f.importe},${f.multas},${f.descuento},${f.monto_declarado},"${f.oficina}","${f.estatus_pago}"`);
    rows.push(`"","","","","TOTALES",${mayorTotalesFiltrados.base_imponible},${mayorTotalesFiltrados.importe},${mayorTotalesFiltrados.multas},${mayorTotalesFiltrados.descuento},${mayorTotalesFiltrados.monto_declarado},"",""`);
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const exportMayorToJSON = () => {
    if (!reporteData) return;
    const blob = new Blob([JSON.stringify({ filas: filteredMayorFilas, totales: mayorTotalesFiltrados }, null, 2)], { type: "application/json" });
    saveAs(blob, `Mayor_Impuesto_${new Date().toISOString().split("T")[0]}.json`);
    toast.success("Exportado a JSON");
  };

  const exportMayorToText = () => {
    if (!reporteData) return;
    let txt = "MAYOR DE IMPUESTO DETALLADO\n" + "=".repeat(60) + "\n\n";
    filteredMayorFilas.forEach(f => {
      txt += `Planilla: ${f.nro_planilla} | RIF: ${f.rif} | ${f.nombre_razon_social}\nFecha: ${f.fecha} | Tipo: ${f.tipo_impuesto}\nBase Imp: ${fmtBs(f.base_imponible)} | Importe: ${f.importe} | Multas: ${fmtBs(f.multas)} | Desc: ${fmtBs(f.descuento)} | Monto: ${fmtBs(f.monto_declarado)}\n` + "-".repeat(60) + "\n";
    });
    txt += `\nTOTALES: Base=${fmtBs(mayorTotalesFiltrados.base_imponible)} | Importe=${mayorTotalesFiltrados.importe.toFixed(2)} | Multas=${fmtBs(mayorTotalesFiltrados.multas)} | Desc=${fmtBs(mayorTotalesFiltrados.descuento)} | Monto=${fmtBs(mayorTotalesFiltrados.monto_declarado)}`;
    const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_${new Date().toISOString().split("T")[0]}.txt`);
    toast.success("Exportado a Texto");
  };

  const exportMayorToHTML = () => {
    if (!reporteData) return;
    let html = `<html><head><meta charset="utf-8"><title>Mayor de Impuesto Detallado</title><style>body{font-family:Arial}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:12px}th{background:#4338CA;color:#fff}tr:nth-child(even){background:#f5f5ff}.totales{font-weight:bold;background:#e0e7ff!important}</style></head><body>`;
    html += `<h2>Mayor de Impuesto Detallado</h2><table><tr><th>Planilla</th><th>RIF</th><th>Razon Social</th><th>Fecha</th><th>Tipo Imp/Tasa</th><th>Base Imponible</th><th>Importe</th><th>Multas</th><th>Descuento</th><th>Monto Declarado</th></tr>`;
    filteredMayorFilas.forEach(f => {
      html += `<tr><td>${f.nro_planilla}</td><td>${f.rif}</td><td>${f.nombre_razon_social}</td><td>${f.fecha}</td><td>${f.tipo_impuesto}</td><td>${fmtBs(f.base_imponible)}</td><td>${f.importe}</td><td>${fmtBs(f.multas)}</td><td>${fmtBs(f.descuento)}</td><td>${fmtBs(f.monto_declarado)}</td></tr>`;
    });
    html += `<tr class="totales"><td colspan="5">TOTALES</td><td>${fmtBs(mayorTotalesFiltrados.base_imponible)}</td><td>${mayorTotalesFiltrados.importe.toFixed(2)}</td><td>${fmtBs(mayorTotalesFiltrados.multas)}</td><td>${fmtBs(mayorTotalesFiltrados.descuento)}</td><td>${fmtBs(mayorTotalesFiltrados.monto_declarado)}</td></tr></table></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `Mayor_Impuesto_${new Date().toISOString().split("T")[0]}.html`);
    toast.success("Exportado a HTML");
  };

  return (
    <div className="space-y-6" data-testid="reporte-mayor-impuesto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Mayor de Impuesto Detallado
            </h1>
            <p className="text-slate-500">Desglose por tipo de impuesto/tasa de cada planilla</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredMayorFilas.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{reporteData?.filas?.length || 0}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Button variant="outline" onClick={() => fetchMayorImpuestoData(false)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="btn-exportar-mayor"><Download className="w-4 h-4 mr-2" />Exportar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportMayorToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportMayorToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportMayorToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportMayorToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> Texto</DropdownMenuItem>
              <DropdownMenuItem onClick={exportMayorToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" /> HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={exportMayorToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Panel de filtros */}
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Tipo de Fecha</Label>
              <Select value={mayorTipoFecha} onValueChange={setMayorTipoFecha}>
                <SelectTrigger className="bg-white" data-testid="mayor-tipo-fecha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planilla">Fecha Planilla</SelectItem>
                  <SelectItem value="recaudacion">Fecha Recaudación</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                <Calendar className="w-4 h-4 inline mr-1" /> Fecha Desde
              </Label>
              <Input type="date" value={mayorFechaDesde} onChange={(e) => setMayorFechaDesde(e.target.value)} className="bg-white" data-testid="mayor-fecha-desde" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                <Calendar className="w-4 h-4 inline mr-1" /> Fecha Hasta
              </Label>
              <Input type="date" value={mayorFechaHasta} onChange={(e) => setMayorFechaHasta(e.target.value)} className="bg-white" data-testid="mayor-fecha-hasta" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Estatus de Pago</Label>
              <Select value={mayorEstatusPago} onValueChange={setMayorEstatusPago}>
                <SelectTrigger className="bg-white" data-testid="mayor-estatus-pago"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Sin pagar">Sin pagar</SelectItem>
                  <SelectItem value="Pagado">Pagado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Oficina</Label>
              <Select value={mayorOficina} onValueChange={setMayorOficina}>
                <SelectTrigger className="bg-white" data-testid="mayor-oficina"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="Hacienda">Hacienda</SelectItem>
                  <SelectItem value="Publicidad">Publicidad</SelectItem>
                  <SelectItem value="Catastro">Catastro</SelectItem>
                  <SelectItem value="Vehículos">Vehiculos</SelectItem>
                  <SelectItem value="Ambulantes">Ambulantes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchMayorImpuestoData(true)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="mayor-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosMayorActivos && (
                <Button variant="outline" onClick={limpiarFiltrosMayor} disabled={loading} data-testid="mayor-btn-limpiar">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Base Imponible</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{fmtBs(reporteData.resumen?.total_base_imponible)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Importe Total</p>
                <p className="text-lg font-bold text-indigo-600 mt-1">{(reporteData.resumen?.total_importe || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Multas</p>
                <p className="text-lg font-bold text-red-600 mt-1">{fmtBs(reporteData.resumen?.total_multas)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Descuento</p>
                <p className="text-lg font-bold text-amber-600 mt-1">{fmtBs(reporteData.resumen?.total_descuento)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Monto Declarado</p>
                <p className="text-lg font-bold text-green-600 mt-1">{fmtBs(reporteData.resumen?.total_monto_declarado)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Distribución por oficina */}
          {reporteData.resumen_por_oficina?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {reporteData.resumen_por_oficina.map((o, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">{o.oficina}</p>
                    <p className="font-semibold text-slate-800">{o.planillas} planillas</p>
                  </div>
                  <span className="text-sm font-bold text-indigo-600">{fmtBs(o.total)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tabla detallada */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Impuestos y Tasas</CardTitle>
                  <CardDescription>{reporteData.resumen?.total_planillas || 0} planillas, {reporteData.resumen?.total_lineas || 0} líneas desglosadas</CardDescription>
                </div>
                {hayFiltrosColumnaActivos && (
                  <Button variant="outline" size="sm" onClick={() => setMayorFilters({ planilla: "", rif: "", razon: "", fecha: "", tipo: "", base: "", importe: "", multas: "", descuento: "", monto: "" })} data-testid="mayor-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="mayor-tabla">
                  <thead>
                    <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Nro Planilla</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">RIF</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Razón Social</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Fecha Liquidación</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Tipo Impuesto/Tasa</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Base Imponible</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Importe</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Multas</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Descuento</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-indigo-800 uppercase">Monto Declarado</th>
                    </tr>
                    <tr className="bg-indigo-50/60 border-b border-indigo-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={mayorFilters.planilla} onChange={(e) => updateMayorFilter("planilla", e.target.value)} data-testid="mayor-filter-planilla" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={mayorFilters.rif} onChange={(e) => updateMayorFilter("rif", e.target.value)} data-testid="mayor-filter-rif" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={mayorFilters.razon} onChange={(e) => updateMayorFilter("razon", e.target.value)} data-testid="mayor-filter-razon" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={mayorFilters.fecha} onChange={(e) => updateMayorFilter("fecha", e.target.value)} data-testid="mayor-filter-fecha" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={mayorFilters.tipo} onChange={(e) => updateMayorFilter("tipo", e.target.value)} data-testid="mayor-filter-tipo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={mayorFilters.base} onChange={(e) => updateMayorFilter("base", e.target.value)} data-testid="mayor-filter-base" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={mayorFilters.importe} onChange={(e) => updateMayorFilter("importe", e.target.value)} data-testid="mayor-filter-importe" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={mayorFilters.multas} onChange={(e) => updateMayorFilter("multas", e.target.value)} data-testid="mayor-filter-multas" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={mayorFilters.descuento} onChange={(e) => updateMayorFilter("descuento", e.target.value)} data-testid="mayor-filter-descuento" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={mayorFilters.monto} onChange={(e) => updateMayorFilter("monto", e.target.value)} data-testid="mayor-filter-monto" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMayorFilas.length === 0 ? (
                      <tr><td colSpan={10} className="text-center py-12 text-slate-400">No se encontraron registros</td></tr>
                    ) : (
                      filteredMayorFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-medium text-slate-900">{f.nro_planilla}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-700">{f.rif}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-700 max-w-[180px] truncate" title={f.nombre_razon_social}>{f.nombre_razon_social}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-600">{f.fecha}</td>
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">{f.tipo_impuesto}</span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.base_imponible)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{(f.importe || 0).toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-red-600">{fmtBs(f.multas)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-amber-600">{fmtBs(f.descuento)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-slate-900">{fmtBs(f.monto_declarado)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredMayorFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-indigo-100 border-t-2 border-indigo-300">
                        <td colSpan={5} className="py-3 px-3 text-sm font-bold text-indigo-900 uppercase">Totales</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-indigo-900">{fmtBs(mayorTotalesFiltrados.base_imponible)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-indigo-900">{mayorTotalesFiltrados.importe.toFixed(2)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(mayorTotalesFiltrados.multas)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-amber-700">{fmtBs(mayorTotalesFiltrados.descuento)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-700">{fmtBs(mayorTotalesFiltrados.monto_declarado)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchMayorImpuestoData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default MayorDetallado;
